<script>
  import { billing, closePurchaseModal, buyPack, loadProducts, refreshBillingAccount } from '../state/billing.svelte.js';
  import { auth } from '../state/auth.svelte.js';
  import { focusDialog } from '../utils/dialog-focus.js';

  let refreshing = $state(false);
  let lastUser = auth.username;

  $effect(() => {
    if (auth.username !== lastUser) {
      lastUser = auth.username;
      billing.purchaseCheckoutUrl = null;
      billing.purchaseError = null;
      billing.purchaseStatus = null;
      billing.activePurchaseId = null;
      billing.purchaseContext = null;
    }
  });

  function fmtSpark(n) {
    if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
    return n.toLocaleString('en-US');
  }

  function close() {
    if (!billing.purchasing && !refreshing) closePurchaseModal();
  }

  async function refreshAccount() {
    refreshing = true;
    billing.purchaseError = null;
    try { await refreshBillingAccount(); }
    catch (error) { billing.purchaseError = error.message; }
    finally { refreshing = false; }
  }
</script>

<div
  class="overlay"
  role="presentation"
  onclick={(event) => { if (event.target === event.currentTarget) close(); }}
>
  <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="purchase-title" tabindex="-1" use:focusDialog={{ onClose: close }}>
    <div class="header">
      <div>
        <h3 id="purchase-title">Buy Premium Spark</h3>
        <p class="sub">Current balance: <strong>{fmtSpark(auth.balance.spark)} Spark</strong> · Premium Spark access: <strong>{auth.balance.premiumSpark > 0 ? 'Available' : 'None'}</strong></p>
      </div>
      <button class="close-btn" onclick={close} disabled={billing.purchasing || refreshing} aria-label="Close purchases">×</button>
    </div>

    <div class="content">
      {#if billing.purchaseContext}
        <p class="sub">Your saved request needs {billing.purchaseContext.currency || 'Spark'}. After checkout, refresh your account and choose to review and resume it.</p>
      {/if}
      {#if billing.productsLoading}
        <div class="center"><span class="spinner"></span> Loading packs…</div>
      {:else if billing.productsError}
        <div class="error">{billing.productsError}</div>
        <button class="refresh" onclick={loadProducts}>Retry loading packs</button>
      {:else if billing.products.length === 0}
        <div class="center muted">No spark packs available right now.</div>
      {:else}
        <div class="pack-grid">
          {#each billing.products as pack (pack.id)}
            <button
              class="pack"
              onclick={() => buyPack(pack.id)}
              disabled={billing.purchasing}
            >
              <span class="pack-spark">{fmtSpark(pack.sparkValue)}</span>
              <span class="pack-label">Premium Spark</span>
              <span class="pack-price">${pack.priceUsd.toFixed(2)}</span>
            </button>
          {/each}
        </div>
      {/if}

      {#if billing.purchaseCheckoutUrl}
        <a class="checkout-link" href={billing.purchaseCheckoutUrl} target="_blank" rel="noopener noreferrer">Open secure checkout</a>
        <p class="sub">If the new tab was blocked, use this link. Your creative work stays in this tab.</p>
      {/if}
      {#if billing.purchaseStatus === 'processing' || billing.purchaseStatus === 'initiated'}
        <div class="status">
          <span class="spinner"></span>
          Complete your purchase in the new tab, then refresh your account. Your saved generation waits for your approval.
        </div>
      {:else if billing.purchaseStatus === 'completed'}
        <div class="status success">✓ Purchase complete — Spark added to your balance.</div>
      {:else if billing.purchaseStatus === 'expired'}
        <div class="status">This checkout expired. Choose a pack to start again.</div>
      {/if}
      {#if billing.purchaseError}<div class="status error" role="alert">{billing.purchaseError}</div>{/if}

      <button class="refresh" onclick={refreshAccount} disabled={billing.purchasing || refreshing}>{refreshing ? 'Refreshing account…' : 'Refresh balance & plan'}</button>

      <p class="footnote">Purchased Spark is Premium Spark and can pay for partner models. Payments are processed securely by Stripe on Sogni.</p>
    </div>
  </div>
</div>

<style>
  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
    padding: 20px;
    animation: fadeIn 0.15s ease;
  }

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .dialog {
    background: var(--color-bg);
    border-radius: 16px;
    width: 100%;
    max-width: 460px;
    box-shadow: var(--shadow-lg);
    border: 2px solid var(--color-primary-border);
    max-height: 92dvh;
    overflow-y: auto;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 20px;
    border-bottom: 1px solid var(--color-border);
    gap: 12px;
  }

  .header h3 { margin: 0; color: var(--color-primary); font-size: 1.2rem; }
  .sub { margin: 4px 0 0; font-size: 0.85rem; color: var(--color-text-muted); }

  .close-btn {
    background: none;
    border: none;
    color: var(--color-text-muted);
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
  }

  button:focus-visible, a:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 3px; }

  .content { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
  .refresh { min-height: 44px; border: 1px solid var(--color-border); border-radius: 8px; padding: 10px; background: var(--color-surface); color: var(--color-text); cursor: pointer; }
  .checkout-link { color: var(--color-primary); font-size: .88rem; }

  .pack-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .pack {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 18px 12px;
    border: 2px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-surface);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .pack:hover:not(:disabled) {
    border-color: var(--color-primary);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 27, 141, 0.15);
  }

  .pack:disabled { opacity: 0.5; cursor: not-allowed; }

  .pack-spark { font-size: 1.5rem; font-weight: 800; color: var(--color-primary); font-variant-numeric: tabular-nums; }
  .pack-label { font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-muted); }
  .pack-price { margin-top: 6px; font-size: 1rem; font-weight: 700; color: var(--color-text); }

  .center { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 24px; }
  .muted { color: var(--color-text-muted); }

  .status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    color: var(--color-text-muted);
    background: var(--color-surface);
    padding: 12px;
    border-radius: 8px;
  }

  .status.success { color: #2b8a3e; background: #ebfbee; }
  .status.error, .error { color: #c92a2a; background: #fff5f5; padding: 12px; border-radius: 8px; font-size: 0.85rem; }

  .footnote { margin: 0; font-size: 0.75rem; color: var(--color-text-muted); text-align: center; }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid var(--color-border);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    flex-shrink: 0;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
</style>
